import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);

  const backgroundImage = "/img/testimonials/temoignages-bg.jpg";

  // Témoignages statiques en attendant la configuration Google
  const staticTestimonials = [
    {
      name: "Lucas C.",
      location: "Annecy",
      rating: 5,
      text: "Nous avons rencontré Angèle dans le cadre d'un projet de naissance physiologique... Quelle découverte ! Une rencontre qui aura marqué notre grossesse et notre accouchement. Que de bienveillance, d'encouragement, de soutien et de bons conseils... tout simplement un grand merci à Angèle pour ces moments qui resteront gravés à jamais dans nos mémoires.",
      service: "Accouchement plateau technique",
      isGoogleReview: true,
      time: new Date("2024-01-15")
    },
    {
      name: "Alice et Lilian",
      location: "Annecy", 
      rating: 5,
      text: "Que dire d'Angèle... La rencontre avec Angèle s'est faite sur le tard, la grossesse déjà bien avancée, dans le cadre de la mise en place d'un plateau technique. L'empathie et le professionnalisme qu'elle dégage nous ont permis de construire une relation de confiance quasi instantanée dans un parcours où celle-ci est primordiale. Elle a été d'une réactivité et d'une aide sans faille face aux premiers signes de travail. Angèle nous a accompagnés jusqu'en salle d'accouchement où elle a manœuvré dans l'ombre pour à la fois respecter l'intimité de ce moment et faire en sorte que tout se passe pour le mieux. Son support, son sourire et son implication resteront un souvenir impérissable pour nous deux. Même après l'accouchement, ses conseils et son expérience sont une mine d'or pour les parents néophytes. Angèle fait partie des superbes rencontres faites pendant cette aventure de la grossesse.",
      service: "Suivi de grossesse",
      isGoogleReview: true,
      time: new Date("2024-01-10")
    },

    {
      name: "Maiwenn G.",
      location: "Annecy",
      rating: 5,
      text: "Nous avons eu la chance immense d’être accompagnés par Angèle pour la naissance de notre fille en plateau technique, et les mots manquent pour exprimer toute notre gratitude. Dès les premiers échanges, son professionnalisme, sa bienveillance et sa joie de vivre nous ont mis en confiance. Durant l’accouchement, elle a fait preuve d’un calme absolu, d’une grande maîtrise, et d’une assurance qui nous a portés tout au long de ce moment unique. Elle veille à tout, avec un vrai souci du détail et une volonté profonde de bien faire. Grâce à elle, nous avons pu vivre une naissance sereine, respectée, et incroyablement puissante. Un immense merci pour cette accompagnante hors du commun, passionnée et profondément humaine. Nous n’aurions pas pu rêver mieux.",
      service: "Accompagnement global",
      isGoogleReview: true,
      time: new Date("2024-01-01")
    },
    {
      name: "Charlotte D.",
      location: "Annecy",
      rating: 5,
      text: "Angèle… La reine des reines des sages-femmes ! Elle nous a accompagné pour la naissance de notre petite dernière en juin 2025 en plateau technique, résultat : une naissance physiologique soutenue de toutes ses compétences, ses tips, et sa présence à la fois douce, discrète, mais aussi dynamique juste quand il le faut ; un post-partum tout doux de tous ses conseils ; une maman, un papa et une bébée comblés de A à Z. Il y a des rencontres qui changent votre vie. Angèle en sera assurément une pour qui croisera sa route. Angèle est une pépite, qui accompagne les familles avec tout son coeur, et ça se sent. Merci de nous avoir fait vivre cet accompagnement parfait dont toutes les familles devraient bénéficier pour accueillir leur bébé.",
      service: "Accouchement physiologique",
      isGoogleReview: true,
      time: new Date("2023-12-20")
    }
  ];

  // Utiliser les témoignages statiques en attendant la configuration Google
  useEffect(() => {
    setGoogleReviews(staticTestimonials);
    setOverallRating(5.0);
    setTotalReviews(staticTestimonials.length);
    setIsLoadingReviews(false);
  }, []);

  // Uniquement les avis Google
  const allTestimonials = googleReviews;

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % allTestimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + allTestimonials.length) % allTestimonials.length);
  };

  return (
    <section id="testimonials" className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-warm-900/70 via-sage-900/60 to-primary-900/50 z-10"></div>
        <img
          src={backgroundImage}
          alt="Petits pieds de bébé"
          loading="lazy"
          className="w-full h-full object-cover opacity-20"
        />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            Témoignages
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl text-warm-100 max-w-3xl mx-auto leading-relaxed"
          >
            Découvrez les expériences de familles que j'ai eu le privilège d'accompagner 
            dans leur parcours de maternité.
          </motion.p>
          
          {/* Message informatif */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mt-4"
          >
          </motion.div>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="max-w-4xl mx-auto">
          {isLoadingReviews ? null : allTestimonials.length === 0 ? (
            <div className="glass-card p-8 rounded-3xl text-center text-warm-100">
              Les avis Google seront bientôt disponibles.
            </div>
          ) : (
          <>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="glass-card px-14 py-8 sm:p-12 rounded-3xl text-center"
              >
                <div className="flex justify-center mb-6">
                  <Quote className="w-12 h-12 text-primary-400" />
                </div>

                <blockquote className="text-lg lg:text-xl text-sage-800 leading-relaxed mb-8 italic">
                  "{allTestimonials[currentIndex].text}"
                </blockquote>

                <div className="space-y-4">
                  {/* Stars */}
                  <div className="flex justify-center space-x-1 mb-4">
                    {[...Array(allTestimonials[currentIndex].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Author */}
                  <div>
                    <p className="font-semibold text-sage-800 text-lg">
                      {allTestimonials[currentIndex].name}
                    </p>
                    <p className="text-sage-600 text-sm">
                      {allTestimonials[currentIndex].location}
                    </p>
                  </div>

                  {/* Service */}
                  <div className="inline-block bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium">
                    {allTestimonials[currentIndex].service}
                  </div>

                  {/* Google Review Badge */}
                  {allTestimonials[currentIndex].isGoogleReview && (
                    <div className="flex items-center justify-center space-x-2 text-xs text-sage-500">
                      <ExternalLink className="w-3 h-3" />
                      <a href="https://g.page/r/CQNzm5sW42EDEAE/review" target="_blank" rel="noopener noreferrer"><span>Avis Google</span></a>
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-3 rounded-full text-white transition-all duration-300 hover:scale-110"
              aria-label="Témoignage précédent"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-3 rounded-full text-white transition-all duration-300 hover:scale-110"
              aria-label="Témoignage suivant"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-3 mt-8">
            {allTestimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentIndex === index 
                    ? 'bg-primary-500 scale-125' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Aller au témoignage ${index + 1}`}
              />
            ))}
          </div>
          </>
          )}
        </div>

        {/* Stats */}
        

        {/* Call to Action for Google Reviews */}
        {totalReviews > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-center mt-12"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-md mx-auto">
              <p className="text-warm-100 mb-4">
                Vous avez été satisfait de mon accompagnement ?
              </p>
              <motion.a
                href="https://g.page/r/CQNzm5sW42EDEAE/review" // Remplacez par votre lien Google
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-300"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Laisser un avis Google
              </motion.a>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;